import { atom, selector } from 'recoil';

import { TOAST_VARIANTS } from '@/components/ui/notifications/toast';
import { notificationsState } from '@/state/notifications';
import { UserNotificationsApiResponse } from '@/types/api-types';
import { getUserNotificationAction } from '@/utils/helpers/user-notifications';

export const userNotificationsState = atom<UserNotificationsApiResponse>({
  key: 'userNotificationsState',
  default: {
    notifications: [],
    count: 0,
    unreadCount: undefined,
  },
});

export const userNotificationsSelectorState = selector<UserNotificationsApiResponse>({
  key: 'userNotificationsSelectorState',
  get: ({ get }) => {
    const userNotifications = get(userNotificationsState);
    return userNotifications;
  },
  set: ({ get, set }, data) => {
    // In app "toast" notification state, not to be confused with these "user" notifications
    const userNotifications = get(userNotificationsState);
    const userNotificationsMarkAsRead = get(userNotificationsMarkAsReadState);

    if (
      userNotifications.unreadCount !== undefined &&
      userNotifications.unreadCount < ((data as UserNotificationsApiResponse)?.unreadCount || 0)
    ) {
      // Assume that the first notification in the array is the new one. Since
      // the user can mark a notification as read/unread, check to make sure it
      // also has never been read before creating the toast notification
      const newNotification = (data as UserNotificationsApiResponse).notifications?.[0];
      if (newNotification && !newNotification.readAt) {
        const { label, href } = getUserNotificationAction(newNotification) || {};
        set(notificationsState, (prevNotifications) => [
          ...prevNotifications,
          {
            id: Date.now(),
            heading: newNotification.notification.title || 'New notification',
            description: newNotification.notification.description || '',
            variant: TOAST_VARIANTS.INFO,
            confirmButtonText: label,
            href,
            onConfirm: () => userNotificationsMarkAsRead(newNotification.id || '', true),
          },
        ]);
      }
    }
    set(userNotificationsState, data);
  },
});

export const userNotificationsIsLoadingState = atom<boolean>({
  key: 'userNotificationsIsLoadingState',
  default: false,
});

export const userNotificationsErrorState = atom<string | undefined>({
  key: 'userNotificationsErrorState',
  default: undefined,
});

export const userNotificationsLoadMoreState = atom<() => void>({
  key: 'userNotificationsLoadMoreState',
  default: undefined,
});

export const userNotificationsMarkAsReadState = atom<(id: string, isRead: boolean) => void>({
  key: 'userNotificationsMarkAsReadState',
  default: undefined,
});

export const userNotificationsMarkAllAsReadState = atom<() => void>({
  key: 'userNotificationsMarkAllAsReadState',
  default: undefined,
});
